import { useState, useRef, useEffect } from 'react';
import { useArtifactStore } from '../hooks/useArtifactStore';
import type { WidgetProps } from './WidgetRegistry';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

const BASE_URL = import.meta.env.VITE_API_URL || '/api';

/**
 * Studio Copilot (KB Doc 139) — docked agent chat that SEES the editor.
 * Every message carries studioContext: the active artifact, the live editor
 * buffer (incl. unsaved edits), the last test result, and the Env Switcher
 * selection. The agent proposes draft versions; promotion stays human-only.
 */
export function CopilotPanelWidget({ journalRootId }: WidgetProps) {
  const { activeArtifact, editorBuffer, testResult, activeDataSources, loadVersions, loadActiveDataSources } = useArtifactStore();
  // The copilot routes to the journal's selected env; make sure that selection is loaded even when
  // the Environment Switcher widget is not on this journal's board.
  useEffect(() => { loadActiveDataSources(journalRootId); }, [journalRootId, loadActiveDataSources]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [status, setStatus] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, status]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput('');
    setMessages(m => [...m, { role: 'user', content: text }, { role: 'assistant', content: '' }]);
    setStreaming(true);
    setStatus('Connecting...');

    const ds = (activeArtifact?.dataSource || '').toLowerCase();
    const product = ds.includes('redshift') || ds.includes('maxsense') ? 'maxsense' : 'cvie';

    const studioContext = {
      artifactId: activeArtifact?.artifactId,
      artifactName: activeArtifact?.name,
      version: activeArtifact?.version,
      editorBuffer: editorBuffer || undefined,
      lastTestResult: testResult ? {
        success: testResult.success, rowCount: testResult.rowCount,
        executionMs: testResult.executionMs, error: testResult.error,
      } : undefined,
      dataSource: activeDataSources[product] || activeArtifact?.dataSource,
      journalId: journalRootId,
    };

    try {
      // Reuse the journal API's auth headers (MSAL token + x-user-id)
      const { copilotHeaders } = await import('../services/api');
      const res = await fetch(`${BASE_URL}/agents/_studio-copilot/stream`, {
        method: 'POST',
        headers: await copilotHeaders(),
        body: JSON.stringify({ input: text, studioContext }),
      });
      if (!res.ok || !res.body) throw new Error(`API ${res.status}: ${await res.text()}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let assistantText = '';

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const evt = JSON.parse(line.slice(6));
            if (evt.type === 'content_block_delta' && evt.delta?.text) {
              assistantText += evt.delta.text;
              setMessages(m => {
                const next = [...m];
                next[next.length - 1] = { role: 'assistant', content: assistantText };
                return next;
              });
              setStatus('');
            } else if (evt.type === 'progress') {
              setStatus(evt.message || evt.phase || '');
            } else if (evt.type === 'message_stop') {
              if (!assistantText && evt.fullText) {
                assistantText = evt.fullText;
                setMessages(m => {
                  const next = [...m];
                  next[next.length - 1] = { role: 'assistant', content: assistantText };
                  return next;
                });
              }
            } else if (evt.type === 'error') {
              throw new Error(evt.error || 'stream error');
            }
          } catch (err) {
            if (err instanceof SyntaxError) continue; // partial JSON line
            throw err;
          }
        }
      }
      // The Copilot may have proposed a draft — refresh versions so the
      // editor banner picks it up immediately.
      if (activeArtifact) loadVersions(activeArtifact.artifactId);
    } catch (err) {
      setMessages(m => {
        const next = [...m];
        next[next.length - 1] = { role: 'assistant', content: `Error: ${err instanceof Error ? err.message : String(err)}` };
        return next;
      });
    } finally {
      setStreaming(false);
      setStatus('');
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-2 py-1 border-b border-tower-border/50 shrink-0 flex items-center gap-2">
        <span className="text-xs text-tower-muted font-semibold">Studio Copilot</span>
        {activeArtifact
          ? <span className="text-[10px] text-success truncate">seeing: {activeArtifact.name} v{activeArtifact.version}</span>
          : <span className="text-[10px] text-tower-muted">select an artifact so I can see your editor</span>}
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-2 space-y-2">
        {messages.length === 0 && (
          <div className="text-[11px] text-tower-muted leading-relaxed">
            Ask me about the SQL you're looking at: <em>"what am I looking at?"</em>,
            <em>"why does this return nothing?"</em>, or <em>"make this show only active diets"</em>.
            I propose drafts — you load, diff, test, and promote.
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`text-[11px] leading-relaxed whitespace-pre-wrap rounded px-2 py-1.5 ${
            m.role === 'user'
              ? 'bg-tower-accent/10 text-tower-text border border-tower-accent/20'
              : 'bg-tower-bg/60 text-tower-text border border-tower-border/40'}`}>
            {m.content || (streaming && i === messages.length - 1 ? '…' : '')}
          </div>
        ))}
        {status && <div className="text-[10px] text-warning animate-pulse">{status}</div>}
      </div>

      <div className="p-2 border-t border-tower-border/50 shrink-0 flex gap-1.5">
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={streaming ? 'Copilot is working…' : 'Ask about or edit the SQL…'}
          disabled={streaming}
          className="flex-1 bg-tower-bg text-tower-text text-xs border border-tower-border rounded px-2 py-1.5 disabled:opacity-50"
        />
        <button
          onClick={send}
          disabled={streaming || !input.trim()}
          className="text-xs px-2.5 py-1.5 bg-success hover:bg-success disabled:bg-tower-border text-textOnPrimary rounded"
        >
          Send
        </button>
      </div>
    </div>
  );
}
